import React, { useEffect, useState } from "react";
import { Heart, Save, Flame, Coins, Cake, Users, ShieldAlert, Check, Ban, Eye, RefreshCw, Activity, AlertTriangle, Sparkles, Trash2 } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const inp = "w-full rounded-lg bg-white/[0.04] border border-white/10 px-3 py-2.5 text-sm focus:outline-none focus:border-brand-red/60";
const lbl = "text-[11px] uppercase tracking-widest text-white/55 font-bold block mb-1 inline-flex items-center gap-1";

export const AdminDatingPanel = () => {
  const [data, setData] = useState({ settings: null, kpis: null });
  const [reports, setReports] = useState([]);
  const [reportFilter, setReportFilter] = useState("open");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ super_free_per_day: 1, super_cost_vola: 50, min_age: 18 });
  const [seedCount, setSeedCount] = useState(500);
  const [seedMode, setSeedMode] = useState("europe_fr");
  const [seeding, setSeeding] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        api.get("/admin/dating/settings"),
        api.get(`/admin/dating/reports?status=${reportFilter}`),
      ]);
      setData({ settings: s.data.settings, kpis: s.data.kpis });
      setForm(s.data.settings);
      setReports(r.data || []);
    } catch (e) { toast.error("Erreur de chargement"); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [reportFilter]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/admin/dating/settings", form);
      toast.success("Paramètres enregistrés ✨");
      loadAll();
    } catch { toast.error("Erreur de sauvegarde"); }
    finally { setSaving(false); }
  };

  const resolve = async (report_id, ban) => {
    if (ban && !window.confirm("Bannir définitivement cet utilisateur des Rencontres ?")) return;
    try {
      await api.post(`/admin/dating/reports/${report_id}/resolve`, { ban });
      toast.success(ban ? "Utilisateur banni" : "Signalement résolu");
      loadAll();
    } catch { toast.error("Erreur"); }
  };

  const seed = async (clear) => {
    const modeLabel = seedMode === "pro_africa" ? "professionnels (Afrique)" : "mixtes (MG/Afrique/diaspora)";
    const msg = clear
      ? `⚠️ Supprime tous les profils seed existants puis crée ${seedCount} nouveaux profils ${modeLabel}. Continuer ?`
      : `Créer ${seedCount} faux profils ${modeLabel} ? (peut prendre 30-60s)`;
    if (!window.confirm(msg)) return;
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/dating/seed", { count: seedCount, mode: seedMode, clear_existing: !!clear });
      toast.success(`${data.created} profils créés${data.skipped ? ` · ${data.skipped} ignorés` : ""}`);
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.error || "Erreur seed"); }
    finally { setSeeding(false); }
  };

  const clearSeed = async () => {
    if (!window.confirm("⚠️ Supprimer TOUS les profils seed (u_seed_*) ? Action irréversible.")) return;
    setSeeding(true);
    try {
      await api.post("/admin/dating/seed", { count: 0, clear_existing: true });
      toast.success("Profils seed nettoyés");
      loadAll();
    } catch { toast.error("Erreur nettoyage"); }
    finally { setSeeding(false); }
  };

  if (loading) return <div className="text-white/55 py-8">Chargement…</div>;
  const k = data.kpis || {};

  return (
    <div className="space-y-8" data-testid="admin-dating-panel">
      {/* KPIs */}
      <section>
        <h3 className="font-display text-lg font-black mb-3 inline-flex items-center gap-2"><Activity size={16} /> Statistiques live</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi icon={Users} label="Profils actifs" value={k.profiles_active} sub={`${k.profiles_total} au total`} />
          <Kpi icon={Heart} label="Matches aujourd'hui" value={k.matches_today} sub={`${k.matches_total} au total`} accent="emerald" />
          <Kpi icon={Eye} label="Swipes aujourd'hui" value={k.swipes_today} sub={`${k.super_today} super`} />
          <Kpi icon={ShieldAlert} label="Signalements ouverts" value={k.reports_open} sub={`${k.banned_users} bannis`} accent={k.reports_open > 0 ? "red" : null} />
        </div>
      </section>

      {/* Settings */}
      <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
        <h3 className="font-display text-lg font-black mb-4 inline-flex items-center gap-2"><Heart size={16} className="text-brand-red" /> Paramètres Rencontres</h3>

        {/* Free mode toggle */}
        <label className={`flex items-center gap-3 p-4 rounded-xl border mb-4 cursor-pointer transition ${form.free_mode ? "bg-emerald-500/10 border-emerald-500/40" : "bg-white/[0.02] border-white/10 hover:bg-white/[0.04]"}`} data-testid="dating-free-mode-toggle">
          <input type="checkbox" checked={!!form.free_mode}
            onChange={(e) => setForm((f) => ({ ...f, free_mode: e.target.checked }))}
            className="accent-emerald-500 w-4 h-4" data-testid="dating-cfg-free-mode" />
          <div className="flex-1">
            <p className="font-bold inline-flex items-center gap-2">
              <Sparkles size={14} className="text-emerald-400" /> Mode 100% gratuit
              {form.free_mode && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/25 text-emerald-300 font-bold uppercase tracking-widest">Activé</span>}
            </p>
            <p className="text-xs text-white/55 mt-0.5">Tous les super-likes sont gratuits, aucun débit Vola, aucun quota journalier. Idéal pour le lancement.</p>
          </div>
        </label>

        <div className={`grid md:grid-cols-3 gap-4 ${form.free_mode ? "opacity-50 pointer-events-none" : ""}`}>
          <div>
            <label className={lbl}><Flame size={11} /> Super-likes gratuits / jour</label>
            <input type="number" min="0" max="20" value={form.super_free_per_day}
              onChange={(e) => setForm((f) => ({ ...f, super_free_per_day: +e.target.value }))}
              className={inp} data-testid="dating-cfg-free" />
            <p className="text-[10px] text-white/45 mt-1">0 = chaque super-like coûte des Vola</p>
          </div>
          <div>
            <label className={lbl}><Coins size={11} /> Coût d'un super-like (Vola)</label>
            <input type="number" min="1" max="100000" value={form.super_cost_vola}
              onChange={(e) => setForm((f) => ({ ...f, super_cost_vola: +e.target.value }))}
              className={inp} data-testid="dating-cfg-cost" />
            <p className="text-[10px] text-white/45 mt-1">Au-delà du quota gratuit</p>
          </div>
          <div>
            <label className={lbl}><Cake size={11} /> Âge minimum</label>
            <input type="number" min="18" max="99" value={form.min_age}
              onChange={(e) => setForm((f) => ({ ...f, min_age: +e.target.value }))}
              className={inp} data-testid="dating-cfg-age" />
            <p className="text-[10px] text-white/45 mt-1">Imposé pendant l'onboarding</p>
          </div>
        </div>
        <button onClick={save} disabled={saving} className="btn-primary mt-4 inline-flex items-center gap-2 text-sm disabled:opacity-50" data-testid="dating-cfg-save">
          {saving ? "Enregistrement…" : <><Save size={14} /> Enregistrer</>}
        </button>
      </section>

      {/* Seed faux profils */}
      <section className="rounded-2xl border border-brand-yellow/20 bg-brand-yellow/[0.04] p-6">
        <h3 className="font-display text-lg font-black mb-2 inline-flex items-center gap-2"><Sparkles size={16} className="text-brand-yellow" /> Faux profils (test)</h3>
        <p className="text-xs text-white/55 mb-4">
          Génère des profils Rencontres réalistes (photos randomuser.me). Identifiés par <code className="text-white/75">u_seed_*</code> et donc supprimables d'un clic.
        </p>
        <div className="flex items-end gap-3 flex-wrap">
          <div className="min-w-[180px]">
            <label className={lbl}>Style</label>
            <select value={seedMode} onChange={(e) => setSeedMode(e.target.value)} className={inp} data-testid="dating-seed-mode">
              <option value="europe_fr">🇫🇷 Europe FR (FR/BE/CH/LU + frontaliers)</option>
              <option value="pro_africa">🌍 Pro · 100% Afrique</option>
              <option value="mixed">Mixte (MG / Afrique / diaspora)</option>
            </select>
          </div>
          <div className="w-[140px]">
            <label className={lbl}>Nombre</label>
            <input type="number" min="1" max="2000" value={seedCount}
              onChange={(e) => setSeedCount(+e.target.value)}
              className={inp} data-testid="dating-seed-count" />
          </div>
          <button onClick={() => seed(false)} disabled={seeding}
            className="btn-primary inline-flex items-center gap-2 text-sm disabled:opacity-50" data-testid="dating-seed-btn">
            {seeding ? "Création…" : <><Sparkles size={14} /> Créer {seedCount} profils</>}
          </button>
          <button onClick={clearSeed} disabled={seeding}
            className="text-xs px-3 py-2 rounded-lg bg-brand-red/15 hover:bg-brand-red/25 border border-brand-red/30 text-brand-red font-bold inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="dating-clear-seed-btn">
            <Trash2 size={12} /> Supprimer tous les seeds
          </button>
        </div>
      </section>

      {/* Reports */}
      <section>
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h3 className="font-display text-lg font-black inline-flex items-center gap-2">
            <ShieldAlert size={16} className={k.reports_open > 0 ? "text-brand-red" : ""} /> Modération · Signalements
          </h3>
          <div className="flex items-center gap-1">
            <FilterBtn active={reportFilter === "open"} onClick={() => setReportFilter("open")} label={`Ouverts (${k.reports_open || 0})`} testid="filter-open" />
            <FilterBtn active={reportFilter === "all"} onClick={() => setReportFilter("all")} label="Tous" testid="filter-all" />
            <button onClick={loadAll} className="p-2 rounded-lg hover:bg-white/5 text-white/55" title="Rafraîchir"><RefreshCw size={14} /></button>
          </div>
        </div>

        {reports.length === 0 ? (
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-8 text-center text-white/55 text-sm">
            {reportFilter === "open" ? "Aucun signalement ouvert ✨" : "Aucun signalement"}
          </div>
        ) : (
          <div className="space-y-2">
            {reports.map((r) => <ReportRow key={r.report_id} r={r} onResolve={resolve} />)}
          </div>
        )}
      </section>
    </div>
  );
};

const Kpi = ({ icon: Icon, label, value, sub, accent }) => {
  const cls = accent === "emerald" ? "border-emerald-500/25 bg-emerald-500/5"
    : accent === "red" ? "border-brand-red/30 bg-brand-red/5"
    : "border-white/10 bg-white/[0.02]";
  return (
    <div className={`rounded-2xl border p-4 ${cls}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/45 font-bold mb-2"><Icon size={11} /> {label}</div>
      <p className="font-display text-3xl font-black tabular-nums">{value ?? 0}</p>
      {sub && <p className="text-[10px] text-white/45 mt-0.5">{sub}</p>}
    </div>
  );
};

const FilterBtn = ({ active, onClick, label, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`text-xs px-3 py-1.5 rounded-full font-bold transition ${active ? "bg-brand-red/20 text-brand-red border border-brand-red/40" : "bg-white/5 text-white/55 border border-white/10 hover:text-white"}`}>
    {label}
  </button>
);

const REASONS = {
  fake: "Faux profil", harassment: "Harcèlement", inappropriate: "Contenu inapproprié",
  minor: "Mineur", spam: "Spam", other: "Autre",
};

const ReportRow = ({ r, onResolve }) => {
  const date = new Date(r.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  return (
    <div className={`rounded-xl border p-4 grid md:grid-cols-[1fr_auto] gap-3 items-center ${r.resolved ? "border-white/5 bg-white/[0.01] opacity-65" : "border-brand-red/20 bg-brand-red/[0.03]"}`} data-testid={`report-${r.report_id}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className="text-xs px-2 py-0.5 rounded-full bg-brand-red/15 text-brand-red border border-brand-red/30 font-bold uppercase tracking-widest">{REASONS[r.reason] || r.reason}</span>
          {r.reported_count > 1 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-brand-yellow/20 text-brand-yellow font-bold">×{r.reported_count} signalements</span>}
          {r.reported_banned && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/30 text-red-200 font-bold inline-flex items-center gap-1"><Ban size={10} /> banni</span>}
          {r.resolved && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-bold inline-flex items-center gap-1"><Check size={10} /> résolu</span>}
          <span className="text-[10px] text-white/40">{date}</span>
        </div>
        <p className="text-sm">
          <span className="text-white/55">Signalé :</span> <strong>{r.reported_name || r.reported_id}</strong>
          {r.reported_email && <span className="text-white/45 text-xs"> · {r.reported_email}</span>}
        </p>
        <p className="text-xs text-white/55 mt-0.5">
          par {r.reporter_name || r.reporter_id}
        </p>
        {r.details && <p className="text-xs text-white/65 mt-2 italic">"{r.details}"</p>}
      </div>
      {!r.resolved && (
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={() => onResolve(r.report_id, false)}
            className="text-xs px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 inline-flex items-center gap-1.5"
            data-testid={`resolve-${r.report_id}`}><Check size={12} /> Résoudre</button>
          {!r.reported_banned && (
            <button onClick={() => onResolve(r.report_id, true)}
              className="text-xs px-3 py-1.5 rounded-lg bg-brand-red/20 hover:bg-brand-red/30 border border-brand-red/40 text-brand-red font-bold inline-flex items-center gap-1.5"
              data-testid={`ban-${r.report_id}`}><Ban size={12} /> Bannir</button>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminDatingPanel;
